/**
 * The hint under a field, written once for every screen that shows it.
 *
 * Root directory, Port, Start Command and Health check each appear twice — on
 * the create form and again in Settings, on the same app, for the same value.
 * They were written twice too, and had drifted: Root directory said it in nine
 * words on one screen and thirty-six on the other, and the Start Command hint
 * pointed at a Port field that is below it in one place and above it in the
 * other. Two texts for one field is two texts to keep true.
 *
 * What stays in these is what changes what you would type. What a health check
 * IS, or that a port is a port, is read once in a lifetime and re-read never,
 * and it was taking a line under the field every time either screen opened.
 *
 * Branch has no entry here, and that is the point: its hint read "The
 * repository branch that your web application uses", under a field labelled
 * Branch. A hint that re-reads its own label is a line of text the eye has to
 * cross to reach the next field, and nothing more.
 */

/**
 * Behind the (i), not under the field.
 *
 * Each of these answers "what IS this?", which is asked on the first app and
 * on no app after it. None of them changes what a person would type: the Root
 * directory field already shows `./`, the Port field already says it detects
 * one, and nobody types a health check path differently for knowing that the
 * request is an HTTP GET.
 */
export const ROOT_DIRECTORY_TIP =
  'The build runs inside this folder and ignores everything outside it. Leave it at ./ to build the whole repository.';

export const PORT_TIP =
  'The port your app listens on inside the container. Back4app reads it from the PORT env var it passes in, so most apps never set this.';

export const START_COMMAND_TIP =
  'Back4app passes the port in as the PORT env var, and routes traffic to it. ' +
  'An app that listens on a port of its own choosing is never reached, and ' +
  'the deploy fails on the health check rather than on anything it printed.';

export const HEALTH_CHECK_TIP =
  'A path Back4app requests during a deployment. The deploy finishes when it answers, so a new version never takes over from a working one before it is up.';



/**
 * The Start Command hint, which is three hints.
 *
 * The first sentence is the one that changes: what happens if the field is
 * left empty, which is a different promise for a static site than for a
 * repository with a Procfile.
 *
 * The PORT rule is the only part that costs a deploy when it is missed, so it
 * is the part a phone keeps and the part that never varies. It no longer says
 * "or set the Port below": the field is below on one screen and above on the
 * other, and a direction that is wrong half the time is worse than no
 * direction — the Port field is on both screens either way.
 */
export function startCommandHelp({
  isStaticSite = false,
  isNarrowScreen = false,
  hasSuggestion = false,
}: {
  isStaticSite?: boolean;
  isNarrowScreen?: boolean;
  /**
   * The analyzer's reading of this repository is shown under the field.
   *
   * When it is, "leave empty and we'll use your Procfile or start script" is
   * the abstract version of a line that is right there naming the command we
   * read. Two sentences, same question, and only one of them is about this
   * repository.
   */
  hasSuggestion?: boolean;
} = {}): string {
  // The PORT rule is not here any more — it is `START_COMMAND_TIP`, behind
  // the (i). It is the one thing on this line that costs a deploy when it is
  // missed, and it was also the one thing that never changed: the same
  // sentence on every app, on both screens, in every state of the field.
  //
  // Nothing at all when the analyzer read the project: the line under the
  // field names the command we would run, which answers "what if I leave this
  // empty" with this repository's own answer.
  if (isNarrowScreen || hasSuggestion) {
    return '';
  }

  const whenEmpty = isStaticSite
    ? "Empty serves your build output as a static site."
    : "Empty uses your Procfile or start script.";

  return whenEmpty;
}
